import * as Api from '@ViewModels';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { TagStepForm } from '../../components/TagStepForm';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import { useTagStepState } from '../../hooks/useTagStepState';
import { useTagStepSave } from '../../hooks/useTagStepSave';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { useStepRenderer } from './hooks/useStepRenderer';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';

interface TagStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function TagStepRenderer({ nodeId, data, readOnly: readOnlyProp }: TagStepRendererProps) {
	const {
		step: tagStep,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
	} = useStepRenderer<Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep>(data, readOnlyProp);

	const { updateNodeData } = useAutomationFlowContext();
	const stepType = data.stepType;
	const schedule = tagStep?.schedule || stepData?.schedule;
	const isAddTag = stepType === Api.AutomationStepType.AddTag;

	const { tagName, setTagName, showRequiredFieldError, autoSave, clearInlineErrorMessage } = useTagStepState({
		tagStep,
		template,
		stepId: stepId || '',
		readonly,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const { save } = useTagStepSave({
		tagStep,
		stepId: stepId || '',
		stepType,
		templateId: template?.id,
		tagName,
		autoSave,
	});

	const syncStepToNodeData = (updatedFields: Partial<Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep>) => {
		if (!updateNodeData || !tagStep) return;
		updateNodeData(nodeId, {
			automationStep: { ...tagStep, ...updatedFields },
		});
	};

	const handleTagChange = (newTagName: string) => {
		setTagName(newTagName);
		clearInlineErrorMessage();
		// Keep node header/compact title in sync while editing.
		syncStepToNodeData({ tagName: newTagName });
	};

	const handleSave = (newTagName: string) => {
		save({ newTagName });
		syncStepToNodeData({ tagName: newTagName });
	};

	const handleScheduleChange = (newSchedule: Api.IAutomationStepSchedule | null) => {
		if (!newSchedule) return;
		save({ newSchedule });
		syncStepToNodeData({ schedule: newSchedule });
	};

	const errorMessage = showRequiredFieldError && !tagName ? 'Please select a tag.' : null;

	const baseKey = `${stepType}-${nodeId}-${tagStep?.id || ''}`;

	return (
		<>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='simple'
				retryCount={autoSave.retryCount}
				errorMessage={autoSave.errorMessage}
			/>
			{tagStep && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={tagStep}
					index={0}
					onScheduleChange={handleScheduleChange}
					parentCaseInfo={data.parentCaseInfo}
				/>
			)}
			<TagStepForm
				tagName={tagName}
				isAddTag={isAddTag}
				canEdit={canEdit}
				errorMessage={errorMessage}
				baseKey={baseKey}
				onTagChange={handleTagChange}
				onSave={handleSave}
				onClearError={clearInlineErrorMessage}
			/>
		</>
	);
}
